import { useSaveTranslation } from '../hooks/useSaveTranslation'
import { AudienceTranslatableField } from './extractAudienceFields'
import { TranslatableField } from './extractTranslatableFields'

// Shared per-field save dispatch. Three call sites (the interactive editor,
// auto-translate and the docx importer) all need to translate a single field
// through the language-aware `useSaveTranslation` mutations — this keeps that
// switch in one place. The import-based path (buildTranslationImportEntries)
// handles Question/Matrix entries in bulk; these helpers cover everything else
// plus the residual where an entry's title is not yet translated. See
// COR-137 / COR-138.

type SaveTranslation = ReturnType<typeof useSaveTranslation>

// Kinds `importQuestionnaireV2` cannot carry safely, so they always take the
// per-field mutations. Anything that slips through is still caught by the
// callers' `consumedKeys` fallback.
export const isPerFieldOnlyKind = (
  kind: TranslatableField['kind']
): boolean => {
  switch (kind) {
    case 'textCardTitle':
    case 'textCardSubtitle':
    case 'textCardBody':
    case 'maxDiffMostLabel':
    case 'maxDiffLeastLabel':
    case 'maxDiffAttributeLabel':
    case 'scaleLabel':
    case 'highlighterQuestionText':
    case 'highlighterContent':
    case 'highlighterCategory':
    case 'conjointText':
    case 'conjointAttributeName':
    case 'conjointAttributeOption':
      return true
    default:
      return false
  }
}

export const isAudienceKind = (
  kind: TranslatableField['kind'] | AudienceTranslatableField['kind']
): boolean => kind === 'audienceQuestionText' || kind === 'audienceOptionValue'

// A conjoint level save resends its attribute's name, resolved from the pre-run
// snapshot, so a level written after a freshly translated name would blank it.
// Writing names last makes the final write the translated one. Shared because
// both bulk callers save in list order.
export const orderPerFieldSaves = <T extends { field: { kind: string } }>(
  jobs: ReadonlyArray<T>
): T[] => [
  ...jobs.filter((job) => job.field.kind !== 'conjointAttributeName'),
  ...jobs.filter((job) => job.field.kind === 'conjointAttributeName')
]

// Returns the underlying mutation promise so callers can await / catch it.
export const dispatchSurveyFieldSave = (
  save: SaveTranslation,
  field: TranslatableField,
  text: string,
  languageCode: string
): Promise<unknown> | undefined => {
  switch (field.kind) {
    case 'questionText':
      return save.saveQuestionText(field.entryId, text, languageCode)
    case 'responseOptionValue':
      if (!field.subId) return undefined
      return save.saveResponseOptionValue(
        field.entryId,
        field.subId,
        text,
        languageCode
      )
    case 'matrixRowText':
      if (!field.subId) return undefined
      return save.saveMatrixRowText(
        field.entryId,
        field.subId,
        text,
        languageCode
      )
    case 'matrixTitle':
      return save.saveMatrixTitle(field.entryId, text, languageCode)
    case 'textCardTitle':
      return save.saveTextCardField(
        field.entryId,
        { title: text },
        languageCode
      )
    case 'textCardSubtitle':
      return save.saveTextCardField(
        field.entryId,
        { subtitle: text },
        languageCode
      )
    case 'textCardBody':
      return save.saveTextCardField(field.entryId, { body: text }, languageCode)
    case 'maxDiffMostLabel':
      return save.saveMaxDiffLabel(
        field.entryId,
        { mostLabel: text },
        languageCode
      )
    case 'maxDiffLeastLabel':
      return save.saveMaxDiffLabel(
        field.entryId,
        { leastLabel: text },
        languageCode
      )
    case 'maxDiffAttributeLabel':
      return save.saveMaxDiffLabel(
        field.entryId,
        { attributeLabel: text },
        languageCode
      )
    case 'scaleLabel': {
      const position = field.scalePosition
      if (position === undefined) return undefined
      return save.saveScaleLabel(field.entryId, position, text, languageCode)
    }
    case 'highlighterQuestionText':
      return save.saveHighlighterText(
        field.entryId,
        { questionText: text },
        languageCode
      )
    case 'highlighterContent':
      return save.saveHighlighterText(
        field.entryId,
        { contentText: text },
        languageCode
      )
    case 'highlighterCategory': {
      const ref = field.highlighterCategory
      if (!field.subId || !ref) return undefined
      return save.saveHighlighterCategory(
        field.entryId,
        {
          responseOptionId: field.subId,
          responseOptionValue: text,
          colour: ref.colour,
          position: ref.position
        },
        languageCode
      )
    }
    case 'conjointText':
      return save.saveConjointText(field.entryId, text, languageCode)
    case 'conjointAttributeName': {
      const ref = field.conjoint
      if (!ref) return undefined
      return save.saveConjointAttribute(
        field.entryId,
        {
          attributeId: ref.attributeId,
          position: ref.attributePosition,
          name: text,
          options: []
        },
        languageCode
      )
    }
    case 'conjointAttributeOption': {
      const ref = field.conjoint
      if (!ref?.optionId || ref.optionPosition === undefined) return undefined
      return save.saveConjointAttribute(
        field.entryId,
        {
          attributeId: ref.attributeId,
          position: ref.attributePosition,
          name: ref.resolveName(languageCode),
          options: [
            {
              attributeOptionId: ref.optionId,
              position: ref.optionPosition,
              value: text
            }
          ]
        },
        languageCode
      )
    }
    default:
      return undefined
  }
}

export const dispatchAudienceFieldSave = (
  save: SaveTranslation,
  field: AudienceTranslatableField,
  text: string,
  languageCode: string
): Promise<unknown> | undefined => {
  switch (field.kind) {
    case 'audienceQuestionText':
      return save.saveAudienceQuestionText(field.saveId, text, languageCode)
    case 'audienceOptionValue':
      if (!field.subId) return undefined
      return save.saveAudienceOptionValue(
        field.saveId,
        field.subId,
        text,
        languageCode
      )
    default:
      return undefined
  }
}
